import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import { ModalGuardProvider } from './contexts/ModalGuardContext';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import { HideAmountsProvider } from './contexts/HideAmountsContext';
import { PrivacyPrefsProvider } from './contexts/PrivacyPrefsContext';
import { PrivacyModeProvider } from './contexts/PrivacyModeContext';
import UpgradeToPortfolioModal from './components/upgrade/UpgradeToPortfolioModal';
import '@/App.css';

// Components
import LandingPage from './components/LandingPage';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AdminForgotPasswordPage from './components/AdminForgotPasswordPage';
import AdminResetPasswordPage from './components/AdminResetPasswordPage';
import AdvisorPortal from './components/AdvisorPortal';
import SharedPortfolioViewer from './components/SharedPortfolioViewer';
import AboutUsPage from './components/AboutUsPage';
import OurPhilosophyPage from './components/OurPhilosophyPage';
import ForAdvisorsPage from './components/ForAdvisorsPage';
import PerspectivesIndex from './components/perspectives/PerspectivesIndex';
import PerspectiveDetail from './components/perspectives/PerspectiveDetail';
import BriefingPage from './components/BriefingPage';
import { SuccessorClaimUpload } from './components/SuccessorClaimPortal';
import PublicWatchPage from './components/PublicWatchPage';
import RegistrationPage from './components/RegistrationPage';
import CookieConsentBanner from './components/CookieConsentBanner';
import SubscriptionGraceBanner from './components/subscription/SubscriptionGraceBanner';
import AutoLockOverlay from './components/AutoLockOverlay';
import BrowserDisguiseApplier from './components/BrowserDisguiseApplier';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import PricingLayers from './pages/PricingLayers';
import EmailVerification from './pages/EmailVerification';
import PaystackCallback from './pages/PaystackCallback';
import ConfirmAction from './pages/ConfirmAction';
import Unsubscribe from './pages/Unsubscribe';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment (not query params) for session_id during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session_token
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/about-us" element={<AboutUsPage />} />
      <Route path="/our-philosophy" element={<OurPhilosophyPage />} />
      <Route path="/for-advisors" element={<ForAdvisorsPage />} />
      <Route path="/perspectives" element={<PerspectivesIndex />} />
      <Route path="/perspectives/:slug" element={<PerspectiveDetail />} />
      <Route path="/briefing" element={<BriefingPage />} />
      <Route path="/access-request/verify/:token" element={<SuccessorClaimUpload />} />
      {/* Legacy alias — old invitation emails point to /successor-claim/*.
          Kept as a silent backward-compat route so previously-sent links still work. */}
      <Route path="/successor-claim/verify/:token" element={<SuccessorClaimUpload />} />
      <Route path="/watch/:videoId" element={<PublicWatchPage />} />
      <Route path="/register" element={<RegistrationPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/:tab" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><Dashboard subscriptionSuccess={true} /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<Navigate to="/dashboard" replace />} />
      <Route path="/subscription/paystack-callback" element={<ProtectedRoute><PaystackCallback /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      
      {/* Advisor Portal */}
      <Route path="/advisor-portal" element={<AdvisorPortal />} />
      
      {/* Shared Portfolio Viewer (public, no auth) */}
      <Route path="/shared-portfolio" element={<SharedPortfolioViewer />} />
      
      {/* Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/pricing-layers" element={<PricingLayers />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      
      {/* Security Actions */}
      <Route path="/confirm-action" element={<ConfirmAction />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <Router>
          <ModalGuardProvider>
            <UpgradeModalProvider>
              <HideAmountsProvider>
                <PrivacyPrefsProvider>
                  <PrivacyModeProvider>
                    <SubscriptionGraceBanner />
                    <AppRouter />
                    <CookieConsentBanner />
                    <UpgradeToPortfolioModal />
                    <AutoLockOverlay />
                    <BrowserDisguiseApplier />
                  </PrivacyModeProvider>
                </PrivacyPrefsProvider>
              </HideAmountsProvider>
            </UpgradeModalProvider>
          </ModalGuardProvider>
        </Router>
        <SonnerToaster richColors position="bottom-right" />
      </HelmetProvider>
    </div>
  );
}

export default App;